import { Controller, useFormContext } from 'react-hook-form';
import type { Label } from '@/types/general';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import useTranslations from '@/hooks/useTranslations';
import { SxProps, Theme } from '@mui/material';

interface TextInputProps {
  field: string;
  label?: Label;
  type?: string;
  readOnly?: boolean;
  placeholder?: Label;
  required?: boolean;
  multiline?: boolean;
  select?: boolean;
  options?: Array<{ value: string; label: Label }>;
  defaultValue: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  customSx?: SxProps<Theme>;
}

export default function TextInput({
  field,
  label,
  type = 'text',
  readOnly = false,
  placeholder = '',
  required = false,
  multiline = false,
  select = false,
  options = [],
  defaultValue = '',
  disabled,
  min,
  max,
  step,
  customSx,
}: TextInputProps) {
  const { t, getLocale } = useTranslations();
  const { control } = useFormContext();

  return (
    <Controller
      name={field as never}
      control={control}
      defaultValue={defaultValue as never}
      render={({ field: controllerField, fieldState: { error } }) => (
        <TextField
          {...controllerField}
          value={controllerField.value || ''}
          label={label ? getLocale(label) : undefined}
          type={type}
          required={required}
          placeholder={getLocale(placeholder) || t('form.enterValue')}
          multiline={multiline}
          select={select}
          margin='none'
          fullWidth
          error={!!error}
          helperText={error?.message}
          sx={{
            ...customSx,
            ...(!label
              ? {
                  '& .MuiInputBase-root': {
                    marginTop: 0,
                  },
                }
              : {}),
          }}
          slotProps={{
            input: {
              readOnly,
            },
            select: {
              notched: undefined,
            },
            htmlInput: {
              min,
              max,
              step,
            },
          }}
          disabled={disabled}
        >
          {select &&
            options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {getLocale(option.label)}
              </MenuItem>
            ))}
        </TextField>
      )}
    />
  );
}
